import {
  DUNGEON_GENERATOR_VERSION,
  type DungeonBlueprint,
  type DungeonMetrics,
  type GeneratedDungeon,
} from './dungeon';
import { MAP_VOID_TILE, type MapData } from './map';
import type { ArpgMonsterArchetypeId } from './arpgMonsters';
import type { TilePoint } from './mobile';

export const COMBAT_LAB_SIZE = 23;
export const COMBAT_LAB_SEED = 713;
export const COMBAT_LAB_PACK_SIZE = 8;
export const COMBAT_LAB_SCENARIO_IDS = [
  'formation',
  'pressure',
  'mobility',
  'elite',
  // The role geometries (PRD #793): one shape of room per thing a monster role
  // has to be watched doing, so a brain can be verified by hand rather than
  // only in a test.
  'open-field',
  'lane',
  'corner',
  'chokepoint',
] as const;
export const COMBAT_LAB_SCENARIO_LIST =
  COMBAT_LAB_SCENARIO_IDS.join(', ');
export type CombatLabScenarioId = (typeof COMBAT_LAB_SCENARIO_IDS)[number];
export const COMBAT_LAB_SOURCE = {
  kind: 'authored',
  id: 'combat-lab',
  revision: 1,
} as const;

const FLOOR_TILES = [
  'land-0x4eb',
  'land-0x4ec',
  'land-0x4ed',
  'land-0x4ee',
] as const;

const EMPTY_METRICS: DungeonMetrics = {
  mainRouteDepth: 0,
  mainRouteTurns: 0,
  mainRouteBranchCount: 0,
  branchCount: 0,
  independentLoops: 0,
  shortestLoopLength: 0,
  deadEndCount: 0,
  maxDeadEndDepth: 0,
  rewardedDeadEndCount: 0,
  minLandmarkSpacing: 0,
  backtrackingBurden: 0,
  packingBacktracks: 0,
  moduleRepetition: 0,
  compactness: 1,
  unrelatedAxisAlignment: 0,
};

export interface CombatLabPlan {
  scenario: CombatLabScenario;
  monsterSpawns: readonly TilePoint[];
  packSize: number;
  /**
   * Cells this scenario walls off inside the square room (PRD #793).
   *
   * The lab was one open box, which is the right room for tuning a swing and
   * the wrong one for watching a monster lose sight of you. A scenario carries
   * its own geometry now, and the room is built around it.
   */
  walls?: readonly TilePoint[];
}

export interface CombatLab extends CombatLabPlan {
  dungeon: GeneratedDungeon;
}

export interface CombatLabScenario {
  id: CombatLabScenarioId;
  label: string;
  description: string;
  active: boolean;
  respawns: boolean;
  enemy: {
    /**
     * The archetype this scenario's targets are, with the fields below as
     * per-scenario overrides on top of it (PRD #793). A lab target is a real
     * monster now: it carries the archetype's loadout and is driven by the
     * archetype's brain, and the numbers here only retune it.
     */
    archetype: ArpgMonsterArchetypeId;
    name: string;
    maxHealth: number;
    damage: number;
    msPerTile: number;
  };
}

function scenarioPlan(
  id: CombatLabScenarioId,
  center: number,
): CombatLabPlan {
  if (id === 'pressure') {
    return {
      scenario: {
        id,
        label: 'Pressure',
        description: 'Four pursuing targets test movement and survival.',
        active: true,
        respawns: true,
        enemy: {
          archetype: 'skeleton-warrior',
          name: 'Pressing Skeleton',
          maxHealth: 70,
          damage: 6,
          msPerTile: 320,
        },
      },
      monsterSpawns: [
        { x: center - 6, y: center },
        { x: center, y: center - 6 },
        { x: center + 6, y: center },
        { x: center, y: center + 6 },
        { x: center - 6, y: center - 6 },
        { x: center + 6, y: center - 6 },
        { x: center + 6, y: center + 6 },
        { x: center - 6, y: center + 6 },
      ],
      packSize: 4,
    };
  }
  if (id === 'mobility') {
    return {
      scenario: {
        id,
        label: 'Mobility',
        description: 'Passive lanes test Charge travel and displacement.',
        active: false,
        respawns: true,
        enemy: {
          archetype: 'skeleton-warrior',
          name: 'Lane Skeleton',
          maxHealth: 70,
          damage: 12,
          msPerTile: 320,
        },
      },
      monsterSpawns: [
        { x: center + 3, y: center },
        { x: center + 5, y: center },
        { x: center + 7, y: center },
        { x: center + 9, y: center },
        { x: center - 3, y: center },
        { x: center - 5, y: center },
        { x: center - 7, y: center },
        { x: center - 9, y: center },
      ],
      packSize: 8,
    };
  }
  if (id === 'elite') {
    return {
      scenario: {
        id,
        label: 'Elite',
        description: 'One durable pursuer tests sustained rotations and survival.',
        active: true,
        respawns: true,
        enemy: {
          archetype: 'skeleton-warrior',
          name: 'Elite Skeleton',
          maxHealth: 700,
          damage: 12,
          msPerTile: 360,
        },
      },
      monsterSpawns: [
        { x: center, y: center - 6 },
        { x: center + 6, y: center },
        { x: center, y: center + 6 },
        { x: center - 6, y: center },
      ],
      packSize: 1,
    };
  }
  if (id === 'formation') {
    return {
      scenario: {
        id,
        label: 'Formation',
        description: 'Two passive packs test targeting and area attacks.',
        active: false,
        respawns: true,
        enemy: {
          archetype: 'skeleton-warrior',
          name: 'Skeleton Warrior',
          maxHealth: 70,
          damage: 12,
          msPerTile: 320,
        },
      },
      monsterSpawns: [
        // Two live 2x2 packs make the opening useful for sweep tuning.
        { x: center - 4, y: center - 4 },
        { x: center - 3, y: center - 4 },
        { x: center - 4, y: center - 3 },
        { x: center - 3, y: center - 3 },
        { x: center + 3, y: center + 3 },
        { x: center + 4, y: center + 3 },
        { x: center + 3, y: center + 4 },
        { x: center + 4, y: center + 4 },
        // Replacements rotate through a third compact pack before cycling back.
        { x: center - 4, y: center + 3 },
        { x: center - 3, y: center + 3 },
        { x: center - 4, y: center + 4 },
        { x: center - 3, y: center + 4 },
      ],
      packSize: COMBAT_LAB_PACK_SIZE,
    };
  }
  if (id === 'open-field') {
    return {
      scenario: {
        id,
        label: 'Open field',
        description:
          'One caster with nothing to hide behind, so a telegraph is the whole fight.',
        active: true,
        respawns: true,
        enemy: {
          archetype: 'ratman-mage',
          name: 'Ratman Mage',
          maxHealth: 40,
          damage: 10,
          msPerTile: 300,
        },
      },
      monsterSpawns: [
        { x: center + 7, y: center },
        { x: center - 7, y: center },
        { x: center, y: center + 7 },
      ],
      packSize: 1,
    };
  }
  if (id === 'lane') {
    // A three-wide corridor: an archer can give ground down it and run out of
    // it, which is where "no kiting forever" is either true or visible.
    const walls: TilePoint[] = [];
    for (let x = 1; x < COMBAT_LAB_SIZE - 1; x += 1) {
      for (let y = 1; y < COMBAT_LAB_SIZE - 1; y += 1) {
        if (Math.abs(y - center) > 1) walls.push({ x, y });
      }
    }
    return {
      scenario: {
        id,
        label: 'Lane',
        description: 'One archer in a corridor: close the distance or be shot.',
        active: true,
        respawns: true,
        enemy: {
          archetype: 'ratman-archer',
          name: 'Ratman Archer',
          maxHealth: 38,
          damage: 9,
          msPerTile: 300,
        },
      },
      monsterSpawns: [
        { x: center + 6, y: center },
        { x: center + 8, y: center },
      ],
      packSize: 1,
      walls,
    };
  }
  if (id === 'corner') {
    // An L of wall with the room open around it: step behind it and a pursuer
    // has to remember where you were rather than see where you are.
    // The elbow sits one cell out from the entrance on both axes, so the player
    // starts inside the open corner rather than inside the wall.
    const walls: TilePoint[] = [];
    for (let offset = -6; offset <= 0; offset += 1) {
      walls.push({ x: center + 1, y: center + 1 + offset });
      walls.push({ x: center + 1 + offset, y: center + 1 });
    }
    return {
      scenario: {
        id,
        label: 'Corner',
        description:
          'A wall to break sight on: pursuit, last-known position, and memory.',
        active: true,
        respawns: true,
        enemy: {
          archetype: 'ratman',
          name: 'Ratman',
          maxHealth: 46,
          damage: 8,
          msPerTile: 280,
        },
      },
      monsterSpawns: [
        { x: center + 4, y: center + 4 },
        { x: center + 6, y: center + 4 },
        { x: center + 4, y: center + 6 },
      ],
      packSize: 2,
      walls,
    };
  }
  if (id === 'chokepoint') {
    const walls = Array.from({ length: COMBAT_LAB_SIZE - 2 }, (_, index) => ({
      x: center,
      y: index + 1,
    })).filter((cell) => cell.y !== center);
    return {
      scenario: {
        id,
        label: 'Chokepoint',
        description:
          'One doorway and a pack behind it: spreading, queueing, and corpses.',
        active: true,
        respawns: true,
        enemy: {
          archetype: 'ratman',
          name: 'Ratman',
          maxHealth: 46,
          damage: 8,
          msPerTile: 280,
        },
      },
      monsterSpawns: [
        { x: center + 3, y: center },
        { x: center + 4, y: center + 1 },
        { x: center + 4, y: center - 1 },
        { x: center + 5, y: center },
        { x: center + 6, y: center + 1 },
        { x: center + 6, y: center - 1 },
      ],
      packSize: 4,
      walls,
    };
  }
  id satisfies never;
  throw new Error(`unknown Combat Lab scenario: ${String(id)}`);
}

/** Read the authored metadata for one Combat Lab scenario. */
export function combatLabScenario(
  id: CombatLabScenarioId,
): CombatLabScenario {
  return scenarioPlan(id, Math.floor(COMBAT_LAB_SIZE / 2)).scenario;
}

/** Build the deterministic square room used to tune live Warrior combat. */
export function makeCombatLab(
  scenarioId: CombatLabScenarioId = 'formation',
): CombatLab {
  const center = Math.floor(COMBAT_LAB_SIZE / 2);
  const roomRegion = {
    x: 1,
    y: 1,
    width: COMBAT_LAB_SIZE - 2,
    height: COMBAT_LAB_SIZE - 2,
  };
  const plan = scenarioPlan(scenarioId, center);
  const blueprint: DungeonBlueprint = {
    source: COMBAT_LAB_SOURCE,
    seed: COMBAT_LAB_SEED,
    generatorVersion: DUNGEON_GENERATOR_VERSION,
    rooms: [{
      id: 'combat-lab',
      moduleId: 'combat-lab-square',
      role: 'ordinary',
      grid: { x: 0, y: 0 },
      region: roomRegion,
      revealRegion: roomRegion,
      reservedZones: [],
      encounterZones: [roomRegion],
      transform: { rotation: 0, mirrored: false },
    }],
    connections: [],
    entrance: {
      roomId: 'combat-lab',
      position: { x: center, y: center },
    },
    exit: {
      roomId: 'combat-lab',
      position: { x: center, y: 0 },
    },
    showExitOnAutomap: false,
    landmarks: [],
  };
  const palette = [MAP_VOID_TILE, ...FLOOR_TILES];
  const cells: number[] = [];
  const blocked: boolean[] = [];
  // The scenario's own geometry, blocked exactly like the perimeter is: the
  // same mask movement walks and sight reads, so a wall in here is a wall in
  // every sense a monster can test (PRD #793).
  const walls = new Set(
    (plan.walls ?? []).map((wall) => `${wall.x},${wall.y}`),
  );
  for (let y = 0; y < COMBAT_LAB_SIZE; y++) {
    for (let x = 0; x < COMBAT_LAB_SIZE; x++) {
      const perimeter =
        x === 0 ||
        y === 0 ||
        x === COMBAT_LAB_SIZE - 1 ||
        y === COMBAT_LAB_SIZE - 1 ||
        walls.has(`${x},${y}`);
      blocked.push(perimeter);
      cells.push(
        perimeter
          ? 0
          : 1 + ((Math.imul(x + 17, 73856093) ^ Math.imul(y + 31, 19349663)) >>> 0) %
              FLOOR_TILES.length,
      );
    }
  }
  const map: MapData = {
    width: COMBAT_LAB_SIZE,
    height: COMBAT_LAB_SIZE,
    palette,
    cells,
    blocked,
    playerSpawn: { ...blueprint.entrance.position },
    dungeon: blueprint,
  };
  return {
    dungeon: {
      ok: true,
      seed: COMBAT_LAB_SEED,
      generatorVersion: DUNGEON_GENERATOR_VERSION,
      attempts: 1,
      map,
      blueprint,
      metrics: { ...EMPTY_METRICS },
    },
    scenario: plan.scenario,
    monsterSpawns: plan.monsterSpawns,
    packSize: plan.packSize,
    ...(plan.walls ? { walls: plan.walls } : {}),
  };
}
